/**
 * components/landing/home/architecture/previews.jsx — the tiny "live"
 * mini-dashboards displayed in the right panel when a module is
 * selected.  Each preview is intentionally small (fits ~340×260) so
 * they can co-exist in the panel without heavy re-layouts.
 *
 * The library is deliberately no-dependency — every visualization is
 * hand-rolled SVG / Tailwind so we don't ship a chart lib.
 */

import React, { useMemo } from 'react'
import clsx from 'clsx'
import {
  Bot, Send, LineChart, Wand2, FlaskConical, Wallet, BarChart3,
  ScanLine, GraduationCap, PieChart, BookOpen, Workflow, CreditCard,
  UsersRound, HandCoins, Building2, Network, ShieldCheck, BellRing,
  ArrowUpRight, ArrowDownRight, CheckCircle2, PlayCircle, Zap,
} from 'lucide-react'
import { MiniSparkline, seededWalk } from '../_shared'

// ── Shared shell (windowless, panel content only) ─────────────────────
const Shell = ({ children, className = '' }) => (
  <div className={clsx(
    'relative flex-1 min-h-[280px] rounded-xl border border-white/10 bg-black/40 p-4 overflow-hidden',
    className,
  )}>
    <div className="pointer-events-none absolute inset-0"
      style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(255,193,7,0.06), transparent 70%)' }} />
    <div className="relative flex flex-col gap-3 h-full">{children}</div>
  </div>
)

const Row = ({ children, className = '' }) => (
  <div className={clsx('flex items-center justify-between', className)}>{children}</div>
)

// ─────────────────────────────────────────────────────────────────────
// AI Mentor
// ─────────────────────────────────────────────────────────────────────
const AiPreview = () => (
  <Shell>
    <Row>
      <div className="flex items-center gap-2 text-xs text-zinc-400"><Bot className="h-3.5 w-3.5 text-brand-gold" />AI Mentor · session</div>
      <span className="text-[10px] text-emerald-400 font-mono">● live</span>
    </Row>
    <div className="flex-1 flex flex-col gap-2 text-xs">
      <div className="self-start max-w-[85%] rounded-lg rounded-tl-none border border-white/10 bg-white/[0.03] px-3 py-2 text-zinc-300">
        Why is my <span className="text-brand-gold">MES</span> strategy underperforming this week?
      </div>
      <div className="self-end max-w-[85%] rounded-lg rounded-tr-none border border-brand-gold/25 bg-brand-gold/[0.08] px-3 py-2 text-zinc-100">
        Regime shifted to <b>low-vol range-bound</b> on Wed. Your breakout entries are firing 3× more but win-rate dropped from 58% → 41%.
        <div className="mt-1.5 text-[10px] text-zinc-400">▸ 2 citations · 1 chart · 1 action</div>
      </div>
    </div>
    <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/50 px-2.5 py-1.5">
      <div className="flex-1 text-[11px] text-zinc-500">Ask the mentor anything…</div>
      <Send className="h-3.5 w-3.5 text-brand-gold" />
    </div>
  </Shell>
)

// ─────────────────────────────────────────────────────────────────────
// Trading Terminal
// ─────────────────────────────────────────────────────────────────────
const TerminalPreview = () => {
  const bids = [4.812, 4.811, 4.810, 4.809, 4.808]
  const asks = [4.813, 4.814, 4.815, 4.816, 4.817]
  return (
    <Shell>
      <Row>
        <div className="flex items-center gap-2 text-xs text-white font-semibold">ES <span className="text-emerald-400 text-[11px] font-mono">4,813.25 +0.42%</span></div>
        <div className="text-[10px] text-zinc-500 font-mono">DOM · 5-deep</div>
      </Row>
      <div className="flex-1 grid grid-cols-2 gap-2 text-[11px] font-mono">
        <div className="flex flex-col gap-0.5">
          {bids.map((p, i) => (
            <div key={p} className="flex items-center justify-between rounded px-2 py-1" style={{ background: `rgba(34,197,94,${0.10 + i * 0.03})` }}>
              <span className="text-emerald-300">{p.toFixed(3)}</span>
              <span className="text-emerald-400">{(120 - i * 18)}</span>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-0.5">
          {asks.map((p, i) => (
            <div key={p} className="flex items-center justify-between rounded px-2 py-1" style={{ background: `rgba(239,68,68,${0.10 + i * 0.03})` }}>
              <span className="text-rose-300">{p.toFixed(3)}</span>
              <span className="text-rose-400">{(90 + i * 22)}</span>
            </div>
          ))}
        </div>
      </div>
      <Row className="text-[11px]">
        <button className="flex-1 mr-1 rounded-md bg-emerald-500/20 py-1.5 text-emerald-300 font-semibold">BUY MKT</button>
        <button className="flex-1 ml-1 rounded-md bg-rose-500/20 py-1.5 text-rose-300 font-semibold">SELL MKT</button>
      </Row>
    </Shell>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Strategy Builder
// ─────────────────────────────────────────────────────────────────────
const StrategyPreview = () => (
  <Shell>
    <Row>
      <div className="text-xs text-white font-semibold">Momentum · v3</div>
      <span className="text-[10px] font-mono text-zinc-500">7 nodes</span>
    </Row>
    <div className="flex-1 flex items-center justify-center">
      <svg viewBox="0 0 300 160" className="w-full h-full">
        {['#3b82f6', '#FFC107', '#22c55e'].map((c, row) => (
          <g key={c}>
            {[0, 1, 2].map((col) => (
              <g key={col}>
                <rect x={30 + col * 90} y={20 + row * 46} width="70" height="28" rx="6" fill="rgba(255,255,255,0.03)" stroke={c} strokeWidth="1" />
                <text x={65 + col * 90} y={38 + row * 46} textAnchor="middle" fontSize="9" fill="#fff" fontFamily="monospace">
                  {row === 0 ? (col === 0 ? 'Signal' : col === 1 ? 'Filter' : 'Sizing') : row === 1 ? (col === 0 ? 'RSI<30' : col === 1 ? 'ATR gate' : 'Kelly') : (col === 0 ? 'Entry' : col === 1 ? 'Stop' : 'Exit')}
                </text>
                {col < 2 && (<line x1={100 + col * 90} y1={34 + row * 46} x2={120 + col * 90} y2={34 + row * 46} stroke={c} strokeWidth="1.2" strokeDasharray="3 3" />)}
              </g>
            ))}
          </g>
        ))}
      </svg>
    </div>
    <Row className="text-[11px] text-zinc-400"><span>Validated · 2m ago</span><span className="text-emerald-400 font-mono">Sharpe 1.72</span></Row>
  </Shell>
)

// ─────────────────────────────────────────────────────────────────────
// Backtesting
// ─────────────────────────────────────────────────────────────────────
const BacktestPreview = () => {
  const data = useMemo(() => seededWalk(7, 60, 6, 100), [])
  return (
    <Shell>
      <Row>
        <div className="text-xs text-white font-semibold">Equity Curve · 2015→2025</div>
        <span className="text-[10px] font-mono text-emerald-400">+287.4%</span>
      </Row>
      <div className="flex-1 rounded-lg border border-white/5 bg-black/30 p-2">
        <MiniSparkline data={data} width={280} height={100} stroke="#22c55e" className="w-full h-full" />
      </div>
      <div className="grid grid-cols-3 gap-2 text-center text-[10px]">
        <Stat k="Sharpe" v="1.72" tone="green" />
        <Stat k="MaxDD" v="-11.4%" tone="orange" />
        <Stat k="WinRate" v="58%" tone="blue" />
      </div>
    </Shell>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Portfolio
// ─────────────────────────────────────────────────────────────────────
const PortfolioPreview = () => (
  <Shell>
    <Row>
      <div className="text-xs text-white font-semibold">Portfolio · $1.24M</div>
      <span className="text-[10px] font-mono text-emerald-400">+2.8% today</span>
    </Row>
    <div className="flex-1 flex flex-col gap-1.5">
      {[
        { s: 'ES',  n: 'S&P 500 fut', p: 42, tone: 'green' },
        { s: 'NQ',  n: 'Nasdaq fut',  p: 28, tone: 'blue' },
        { s: 'GC',  n: 'Gold fut',    p: 14, tone: 'orange' },
        { s: 'CL',  n: 'Crude fut',   p: 10, tone: 'purple' },
        { s: 'CASH', n: '',            p:  6, tone: 'gray' },
      ].map((r) => (
        <div key={r.s} className="flex items-center gap-2 text-[11px]">
          <span className="w-8 font-mono text-white">{r.s}</span>
          <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
            <div className={clsx('h-full rounded-full',
              r.tone === 'green'  && 'bg-emerald-400',
              r.tone === 'blue'   && 'bg-sky-400',
              r.tone === 'orange' && 'bg-brand-gold',
              r.tone === 'purple' && 'bg-violet-400',
              r.tone === 'gray'   && 'bg-zinc-500',
            )} style={{ width: `${r.p}%` }} />
          </div>
          <span className="w-8 text-right font-mono text-zinc-300">{r.p}%</span>
        </div>
      ))}
    </div>
    <div className="text-[10px] text-zinc-500 font-mono">VaR (95%) · $12,320 · 0.99% NAV</div>
  </Shell>
)

// ─────────────────────────────────────────────────────────────────────
// Market Viewer
// ─────────────────────────────────────────────────────────────────────
const MarketViewerPreview = () => {
  const a = useMemo(() => seededWalk(11, 60, 8, 100), [])
  const b = useMemo(() => seededWalk(19, 60, 8, 100), [])
  return (
    <Shell>
      <Row>
        <div className="text-xs text-white font-semibold">Sync-Linked Charts</div>
        <span className="text-[10px] font-mono text-zinc-500">1m · 5m · 15m</span>
      </Row>
      <div className="flex-1 grid grid-rows-2 gap-2">
        <div className="rounded-lg border border-white/5 bg-black/30 p-1.5">
          <div className="text-[9px] font-mono text-emerald-400 pl-1">ES · 1m</div>
          <MiniSparkline data={a} width={280} height={50} stroke="#22c55e" className="w-full" />
        </div>
        <div className="rounded-lg border border-white/5 bg-black/30 p-1.5">
          <div className="text-[9px] font-mono text-sky-400 pl-1">NQ · 1m</div>
          <MiniSparkline data={b} width={280} height={50} stroke="#3b82f6" className="w-full" />
        </div>
      </div>
    </Shell>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Scanner
// ─────────────────────────────────────────────────────────────────────
const ScannerPreview = () => (
  <Shell>
    <Row>
      <div className="text-xs text-white font-semibold">Scanner · 6,204 symbols</div>
      <span className="text-[10px] font-mono text-emerald-400">14 alerts / min</span>
    </Row>
    <div className="flex-1 flex flex-col gap-1 font-mono text-[11px]">
      {[
        { s: 'AAPL', sc: 92, r: 'Breakout · vol > 3×', tone: 'green' },
        { s: 'TSLA', sc: 88, r: 'Bull flag · 15m', tone: 'green' },
        { s: 'AMD',  sc: 79, r: 'IV crush · post-ER', tone: 'orange' },
        { s: 'MSFT', sc: 71, r: 'Mean reversion', tone: 'blue' },
      ].map((r, i) => (
        <div key={r.s} className="flex items-center gap-2 rounded-md border border-white/5 bg-white/[0.02] px-2 py-1.5">
          <span className={clsx('inline-flex h-6 w-6 items-center justify-center rounded text-[10px] font-bold',
            r.tone === 'green'  && 'bg-emerald-500/20 text-emerald-300',
            r.tone === 'orange' && 'bg-brand-gold/20 text-brand-gold',
            r.tone === 'blue'   && 'bg-sky-500/20 text-sky-300',
          )}>{r.sc}</span>
          <span className="w-12 text-white">{r.s}</span>
          <span className="flex-1 text-zinc-400">{r.r}</span>
          <ArrowUpRight className="h-3 w-3 text-zinc-500" />
        </div>
      ))}
    </div>
  </Shell>
)

// ─────────────────────────────────────────────────────────────────────
// Trading Academy
// ─────────────────────────────────────────────────────────────────────
const AcademyPreview = () => (
  <Shell>
    <Row>
      <div className="text-xs text-white font-semibold">Level 3 · Risk Management</div>
      <span className="text-[10px] font-mono text-emerald-400">67% complete</span>
    </Row>
    <div className="flex-1 flex flex-col gap-2 text-[11px]">
      {[
        { t: 'Position sizing', d: true },
        { t: 'Kelly criterion in practice', d: true },
        { t: 'Portfolio-level VaR', d: false, active: true },
        { t: 'Regime-based sizing', d: false },
      ].map((r) => (
        <div key={r.t} className={clsx('flex items-center gap-2 rounded-md border px-2.5 py-2',
          r.active ? 'border-brand-gold/30 bg-brand-gold/[0.06]' : 'border-white/10 bg-white/[0.02]',
        )}>
          {r.d
            ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
            : r.active ? <PlayCircle className="h-3.5 w-3.5 text-brand-gold" /> : <div className="h-3.5 w-3.5 rounded-full border border-white/20" />}
          <span className={clsx('flex-1', r.active ? 'text-white' : 'text-zinc-400')}>{r.t}</span>
          {r.d && <span className="text-[9px] font-mono text-zinc-500">100%</span>}
        </div>
      ))}
    </div>
    <div className="h-1 rounded-full bg-white/5 overflow-hidden">
      <div className="h-full w-2/3 bg-gradient-to-r from-brand-gold to-[#FFE17A]" />
    </div>
  </Shell>
)

// ─────────────────────────────────────────────────────────────────────
// Analytics
// ─────────────────────────────────────────────────────────────────────
const AnalyticsPreview = () => {
  const w = useMemo(() => seededWalk(23, 40, 6, 40), [])
  return (
    <Shell>
      <Row>
        <div className="text-xs text-white font-semibold">Analytics · Live desk</div>
        <span className="text-[10px] font-mono text-emerald-400">● realtime</span>
      </Row>
      <div className="grid grid-cols-3 gap-2">
        <Stat k="Traders"  v="128"  tone="orange" />
        <Stat k="Volume"   v="$4.8M" tone="green" />
        <Stat k="Alerts"   v="284"  tone="blue" />
      </div>
      <div className="flex-1 rounded-lg border border-white/5 bg-black/30 p-2">
        <MiniSparkline data={w} width={280} height={72} stroke="#3b82f6" className="w-full h-full" />
      </div>
      <div className="text-[10px] font-mono text-zinc-500">Cohort · Q3 desk vs Q2 · +14.8%</div>
    </Shell>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Knowledge Base
// ─────────────────────────────────────────────────────────────────────
const KbPreview = () => (
  <Shell>
    <Row>
      <div className="text-xs text-white font-semibold">Knowledge Base · 812 docs</div>
      <span className="text-[10px] font-mono text-zinc-500">Vector · text-embedding-3-small</span>
    </Row>
    <div className="flex-1 flex flex-col gap-1.5">
      {[
        { t: 'Strategy playbook · v14',  s: 'PDF · 42 pages', ok: true },
        { t: 'Risk framework · 2025',    s: 'DOCX · 18 pages', ok: true },
        { t: 'Broker integration specs',  s: 'MD · 12 files', ok: true },
        { t: 'Q3 desk retros',            s: 'MD · 6 files', ok: true },
      ].map((r) => (
        <div key={r.t} className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-2.5 py-2 text-[11px]">
          <BookOpen className="h-3.5 w-3.5 text-brand-gold shrink-0" />
          <span className="flex-1 text-white truncate">{r.t}</span>
          <span className="text-[9px] font-mono text-zinc-500">{r.s}</span>
        </div>
      ))}
    </div>
    <div className="text-[10px] font-mono text-emerald-400">● indexed · 2s ago</div>
  </Shell>
)

// ─────────────────────────────────────────────────────────────────────
// Automation
// ─────────────────────────────────────────────────────────────────────
const AutomationPreview = () => (
  <Shell>
    <Row>
      <div className="text-xs text-white font-semibold">Playbook · Risk-off gate</div>
      <span className="text-[10px] font-mono text-emerald-400">● armed</span>
    </Row>
    <div className="flex-1 flex flex-col gap-1.5 text-[11px]">
      {[
        { i: Zap, t: 'Trigger · VIX > 22', tone: 'orange' },
        { i: ShieldCheck, t: 'Guard · CIO approval required', tone: 'blue' },
        { i: Workflow, t: 'Action · reduce all books 25%', tone: 'green' },
        { i: BellRing, t: 'Notify · #risk-desk + email', tone: 'orange' },
      ].map((r, i) => (
        <div key={i} className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-2.5 py-2">
          <r.i className={clsx('h-3.5 w-3.5',
            r.tone === 'orange' && 'text-brand-gold',
            r.tone === 'blue'   && 'text-sky-400',
            r.tone === 'green'  && 'text-emerald-400',
          )} />
          <span className="flex-1 text-white">{r.t}</span>
          {i < 3 && <span className="text-[9px] font-mono text-zinc-500">→</span>}
        </div>
      ))}
    </div>
  </Shell>
)

// ─────────────────────────────────────────────────────────────────────
// Billing
// ─────────────────────────────────────────────────────────────────────
const BillingPreview = () => (
  <Shell>
    <Row>
      <div className="text-xs text-white font-semibold">Billing · Enterprise plan</div>
      <span className="text-[10px] font-mono text-emerald-400">Active</span>
    </Row>
    <div className="grid grid-cols-2 gap-2">
      <Stat k="MRR" v="$28,400" tone="green" />
      <Stat k="Seats" v="112" tone="orange" />
    </div>
    <div className="flex-1 flex flex-col gap-1.5 text-[11px]">
      {[
        { d: 'Invoice #3247', a: '$4,200.00', s: 'Paid',   tone: 'green' },
        { d: 'Metered · API', a: '$412.30',   s: 'Draft',  tone: 'orange' },
        { d: 'Refund · seat', a: '-$120.00',  s: 'Issued', tone: 'blue' },
      ].map((r, i) => (
        <div key={i} className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-2.5 py-2">
          <CreditCard className="h-3.5 w-3.5 text-brand-gold shrink-0" />
          <span className="flex-1 text-white">{r.d}</span>
          <span className="font-mono text-white">{r.a}</span>
          <span className={clsx('text-[9px] font-mono uppercase tracking-wider',
            r.tone === 'green'  && 'text-emerald-400',
            r.tone === 'orange' && 'text-brand-gold',
            r.tone === 'blue'   && 'text-sky-400',
          )}>{r.s}</span>
        </div>
      ))}
    </div>
  </Shell>
)

// ─────────────────────────────────────────────────────────────────────
// CRM
// ─────────────────────────────────────────────────────────────────────
const CrmPreview = () => (
  <Shell>
    <Row>
      <div className="text-xs text-white font-semibold">Pipeline · Q3</div>
      <span className="text-[10px] font-mono text-emerald-400">$4.2M weighted</span>
    </Row>
    <div className="flex-1 grid grid-cols-4 gap-1.5 text-[10px]">
      {[
        { s: 'Lead',  n: 42, tone: 'gray' },
        { s: 'Qual',  n: 24, tone: 'blue' },
        { s: 'Prop',  n: 12, tone: 'orange' },
        { s: 'Close', n:  7, tone: 'green' },
      ].map((c) => (
        <div key={c.s} className="rounded-md border border-white/10 bg-white/[0.02] p-2 flex flex-col gap-1">
          <div className={clsx('font-mono uppercase tracking-wider text-[9px]',
            c.tone === 'gray'   && 'text-zinc-400',
            c.tone === 'blue'   && 'text-sky-400',
            c.tone === 'orange' && 'text-brand-gold',
            c.tone === 'green'  && 'text-emerald-400',
          )}>{c.s}</div>
          <div className="text-lg font-semibold text-white">{c.n}</div>
          <div className={clsx('h-1 rounded-full',
            c.tone === 'gray'   && 'bg-zinc-400/40',
            c.tone === 'blue'   && 'bg-sky-400/60',
            c.tone === 'orange' && 'bg-brand-gold/60',
            c.tone === 'green'  && 'bg-emerald-400/60',
          )} />
        </div>
      ))}
    </div>
    <div className="text-[10px] font-mono text-zinc-500">Auto-nurture · 3 stages · 8 templates</div>
  </Shell>
)

// ─────────────────────────────────────────────────────────────────────
// Affiliate
// ─────────────────────────────────────────────────────────────────────
const AffiliatePreview = () => (
  <Shell>
    <Row>
      <div className="text-xs text-white font-semibold">Partners · 214 active</div>
      <span className="text-[10px] font-mono text-emerald-400">+18 this week</span>
    </Row>
    <div className="grid grid-cols-3 gap-2">
      <Stat k="Refs"     v="1,842"  tone="orange" />
      <Stat k="Conv."    v="14.2%"  tone="green" />
      <Stat k="Payouts"  v="$68K"   tone="blue" />
    </div>
    <div className="flex-1 flex flex-col gap-1.5 text-[11px]">
      {[
        { p: 'Prop Firm A', r: 'Gold',   e: '$4,120' },
        { p: 'Streamer B',   r: 'Silver', e: '$1,940' },
        { p: 'Community C', r: 'Bronze', e: '$820'   },
      ].map((r) => (
        <div key={r.p} className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-2.5 py-2">
          <HandCoins className="h-3.5 w-3.5 text-brand-gold" />
          <span className="flex-1 text-white">{r.p}</span>
          <span className="text-[9px] uppercase tracking-wider font-mono text-zinc-500">{r.r}</span>
          <span className="font-mono text-emerald-400">{r.e}</span>
        </div>
      ))}
    </div>
  </Shell>
)

// ─────────────────────────────────────────────────────────────────────
// White-Label
// ─────────────────────────────────────────────────────────────────────
const WhiteLabelPreview = () => (
  <Shell>
    <Row>
      <div className="text-xs text-white font-semibold">Tenant control</div>
      <span className="text-[10px] font-mono text-emerald-400">4 tenants live</span>
    </Row>
    <div className="flex-1 grid grid-cols-2 gap-2 text-[11px]">
      {[
        { n: 'AlphaTrade Pro',  d: 'alpha.io',      s: 'live',   tone: 'green' },
        { n: 'DesQuant Labs',   d: 'desquant.ai',   s: 'live',   tone: 'green' },
        { n: 'RiskDesk EU',     d: 'riskdesk.eu',   s: 'staging', tone: 'orange' },
        { n: 'YourBrand',       d: 'you.kalkiai.trade', s: 'draft',  tone: 'blue' },
      ].map((t) => (
        <div key={t.n} className="rounded-md border border-white/10 bg-white/[0.02] p-2 flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <span className="text-white font-semibold truncate">{t.n}</span>
            <span className={clsx('h-1.5 w-1.5 rounded-full',
              t.tone === 'green' && 'bg-emerald-400',
              t.tone === 'orange' && 'bg-brand-gold',
              t.tone === 'blue' && 'bg-sky-400',
            )} />
          </div>
          <div className="text-[9px] font-mono text-zinc-500">{t.d}</div>
          <div className={clsx('text-[9px] uppercase tracking-wider font-mono',
            t.tone === 'green'  && 'text-emerald-400',
            t.tone === 'orange' && 'text-brand-gold',
            t.tone === 'blue'   && 'text-sky-400',
          )}>{t.s}</div>
        </div>
      ))}
    </div>
  </Shell>
)

// ─────────────────────────────────────────────────────────────────────
// API Gateway
// ─────────────────────────────────────────────────────────────────────
const ApiPreview = () => (
  <Shell>
    <Row>
      <div className="text-xs text-white font-semibold">API Gateway · v2</div>
      <span className="text-[10px] font-mono text-emerald-400">p99 · 84ms</span>
    </Row>
    <div className="flex-1 flex flex-col gap-1 font-mono text-[10.5px]">
      {[
        { m: 'GET',  p: '/api/v2/portfolio',    ms: 42, s: 200 },
        { m: 'POST', p: '/api/v2/orders',        ms: 91, s: 201 },
        { m: 'GET',  p: '/api/v2/market/scan',   ms: 128, s: 200 },
        { m: 'GET',  p: '/api/v2/ai/mentor/ask', ms: 632, s: 200 },
      ].map((r, i) => (
        <div key={i} className="flex items-center gap-2 rounded-md border border-white/5 bg-black/30 px-2 py-1.5">
          <span className={clsx('w-10 text-center px-1 py-0.5 rounded text-[9px] font-bold',
            r.m === 'GET'  && 'bg-emerald-500/20 text-emerald-300',
            r.m === 'POST' && 'bg-sky-500/20 text-sky-300',
          )}>{r.m}</span>
          <span className="flex-1 text-zinc-300 truncate">{r.p}</span>
          <span className="text-zinc-400">{r.ms}ms</span>
          <span className="text-emerald-400">{r.s}</span>
        </div>
      ))}
    </div>
    <div className="text-[10px] font-mono text-zinc-500">1,284,921 requests / 24h</div>
  </Shell>
)

// ─────────────────────────────────────────────────────────────────────
// Security
// ─────────────────────────────────────────────────────────────────────
const SecurityPreview = () => (
  <Shell>
    <Row>
      <div className="text-xs text-white font-semibold">Security posture</div>
      <span className="text-[10px] font-mono text-emerald-400">● healthy</span>
    </Row>
    <div className="grid grid-cols-2 gap-2">
      <Stat k="SSO users"  v="118" tone="green" />
      <Stat k="Roles"      v="24"  tone="blue" />
    </div>
    <div className="flex-1 flex flex-col gap-1.5 text-[11px]">
      {[
        { t: 'SSO · Okta',                   ok: true },
        { t: 'MFA · TOTP + WebAuthn',         ok: true },
        { t: 'Audit log · 90 days retention', ok: true },
        { t: 'Encryption · AES-256 at rest',  ok: true },
      ].map((r) => (
        <div key={r.t} className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-2.5 py-1.5">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
          <span className="flex-1 text-white">{r.t}</span>
          <CheckCircle2 className="h-3 w-3 text-emerald-400" />
        </div>
      ))}
    </div>
  </Shell>
)

// ─────────────────────────────────────────────────────────────────────
// Notifications
// ─────────────────────────────────────────────────────────────────────
const NotificationsPreview = () => (
  <Shell>
    <Row>
      <div className="text-xs text-white font-semibold">Delivery · last 60m</div>
      <span className="text-[10px] font-mono text-emerald-400">98.4% success</span>
    </Row>
    <div className="grid grid-cols-4 gap-1.5">
      {[
        { c: 'Email', n: 2140, tone: 'blue' },
        { c: 'Push',  n: 1284, tone: 'orange' },
        { c: 'SMS',   n:  412, tone: 'green' },
        { c: 'InApp', n: 3720, tone: 'purple' },
      ].map((c) => (
        <div key={c.c} className="rounded-md border border-white/10 bg-white/[0.02] p-2 flex flex-col gap-0.5">
          <div className="text-[9px] uppercase tracking-wider font-mono text-zinc-500">{c.c}</div>
          <div className="text-sm font-semibold text-white">{c.n.toLocaleString()}</div>
          <div className={clsx('h-1 rounded-full',
            c.tone === 'blue'   && 'bg-sky-400/60',
            c.tone === 'orange' && 'bg-brand-gold/60',
            c.tone === 'green'  && 'bg-emerald-400/60',
            c.tone === 'purple' && 'bg-violet-400/60',
          )} />
        </div>
      ))}
    </div>
    <div className="flex-1 flex flex-col gap-1.5 text-[11px]">
      {[
        { t: 'Trade fill · ES @ 4,812.75',    s: '2s ago' },
        { t: 'Risk alert · CL drawdown -3.2%', s: '14s ago' },
        { t: 'Mentor · new insight · Regime',  s: '1m ago' },
      ].map((r) => (
        <div key={r.t} className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-2.5 py-1.5">
          <BellRing className="h-3.5 w-3.5 text-brand-gold" />
          <span className="flex-1 text-white truncate">{r.t}</span>
          <span className="text-[9px] font-mono text-zinc-500">{r.s}</span>
        </div>
      ))}
    </div>
  </Shell>
)

// ─── Little stat block reused in previews ─────────────────────────────
const Stat = ({ k, v, tone = 'orange' }) => (
  <div className="rounded-md border border-white/10 bg-white/[0.02] px-2 py-1.5">
    <div className={clsx('text-[9px] uppercase tracking-wider font-mono',
      tone === 'orange' && 'text-brand-gold',
      tone === 'green'  && 'text-emerald-400',
      tone === 'blue'   && 'text-sky-400',
      tone === 'purple' && 'text-violet-400',
    )}>{k}</div>
    <div className="text-sm font-semibold text-white">{v}</div>
  </div>
)

// ─── Registry ─────────────────────────────────────────────────────────
export const PREVIEWS = {
  ai:            AiPreview,
  terminal:      TerminalPreview,
  strategy:      StrategyPreview,
  backtest:      BacktestPreview,
  portfolio:     PortfolioPreview,
  marketviewer:  MarketViewerPreview,
  scanner:       ScannerPreview,
  academy:       AcademyPreview,
  analytics:     AnalyticsPreview,
  kb:            KbPreview,
  automation:    AutomationPreview,
  billing:       BillingPreview,
  crm:           CrmPreview,
  affiliate:     AffiliatePreview,
  whitelabel:    WhiteLabelPreview,
  api:           ApiPreview,
  security:      SecurityPreview,
  notifications: NotificationsPreview,
}
